/* Enum degerlerinin Turkce karsiliklari.
   Backend enum'lari dize olarak gonderir; arayuz burada insan diline cevirir. */

import type {
  BulkOperationStatus,
  ContactMessageStatus,
  DiscountType,
  FinancialStatus,
  FulfillmentStatus,
  OrderStatus,
  PaymentMethodType,
  ProductStatus,
  RefundStatus,
  ReturnDisposition,
  ReturnReason,
  ReturnStatus,
  ReviewStatus,
  ShipmentTrackingStatus,
} from '../api/types';

type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface StatusLabel {
  text: string;
  tone: Tone;
}

const toneClass: Record<Tone, string> = {
  neutral: 'bg-ink-100 text-ink-700',
  info: 'bg-brand-50 text-brand-800',
  success: 'bg-emerald-50 text-emerald-800',
  warning: 'bg-amber-50 text-amber-800',
  danger: 'bg-red-50 text-red-700',
};

export function badgeClass(tone: Tone): string {
  return toneClass[tone];
}

export const orderStatusLabels: Record<OrderStatus, StatusLabel> = {
  Draft: { text: 'Taslak', tone: 'neutral' },
  Open: { text: 'Açık', tone: 'info' },
  Archived: { text: 'Arşivlendi', tone: 'neutral' },
  Cancelled: { text: 'İptal edildi', tone: 'danger' },
};

export const financialStatusLabels: Record<FinancialStatus, StatusLabel> = {
  Pending: { text: 'Ödeme bekliyor', tone: 'warning' },
  Authorized: { text: 'Provizyon alındı', tone: 'info' },
  PartiallyPaid: { text: 'Kısmen ödendi', tone: 'warning' },
  Paid: { text: 'Ödendi', tone: 'success' },
  PartiallyRefunded: { text: 'Kısmen iade', tone: 'warning' },
  Refunded: { text: 'İade edildi', tone: 'neutral' },
  Voided: { text: 'İptal edildi', tone: 'danger' },
  Expired: { text: 'Süresi doldu', tone: 'danger' },
};

export const fulfillmentStatusLabels: Record<FulfillmentStatus, StatusLabel> = {
  Unfulfilled: { text: 'Hazırlanıyor', tone: 'warning' },
  PartiallyFulfilled: { text: 'Kısmen sevk edildi', tone: 'info' },
  Fulfilled: { text: 'Sevk edildi', tone: 'success' },
  Restocked: { text: 'Stoğa geri alındı', tone: 'neutral' },
  Cancelled: { text: 'İptal', tone: 'danger' },
  OnHold: { text: 'Beklemede', tone: 'warning' },
};

export const productStatusLabels: Record<ProductStatus, StatusLabel> = {
  Draft: { text: 'Taslak', tone: 'neutral' },
  Active: { text: 'Yayında', tone: 'success' },
  Archived: { text: 'Arşiv', tone: 'neutral' },
};

export const returnStatusLabels: Record<ReturnStatus, StatusLabel> = {
  Requested: { text: 'Talep edildi', tone: 'warning' },
  Approved: { text: 'Onaylandı', tone: 'info' },
  Rejected: { text: 'Reddedildi', tone: 'danger' },
  InTransit: { text: 'Yolda', tone: 'info' },
  Received: { text: 'Teslim alındı', tone: 'info' },
  Inspecting: { text: 'İnceleniyor', tone: 'info' },
  Completed: { text: 'Tamamlandı', tone: 'success' },
  Cancelled: { text: 'İptal edildi', tone: 'neutral' },
};

export const refundStatusLabels: Record<RefundStatus, StatusLabel> = {
  Pending: { text: 'Ödeme bekliyor', tone: 'warning' },
  Processing: { text: 'İşleniyor', tone: 'info' },
  Completed: { text: 'Tamamlandı', tone: 'success' },
  Failed: { text: 'Başarısız', tone: 'danger' },
  Cancelled: { text: 'İptal edildi', tone: 'neutral' },
};

export const shipmentStatusLabels: Record<ShipmentTrackingStatus, StatusLabel> = {
  Unknown: { text: 'Bilinmiyor', tone: 'neutral' },
  LabelCreated: { text: 'Etiket oluşturuldu', tone: 'neutral' },
  PickedUp: { text: 'Kargoya verildi', tone: 'info' },
  InTransit: { text: 'Yolda', tone: 'info' },
  AtDeliveryBranch: { text: 'Dağıtım şubesinde', tone: 'info' },
  OutForDelivery: { text: 'Dağıtımda', tone: 'info' },
  Delivered: { text: 'Teslim edildi', tone: 'success' },
  AttemptedDelivery: { text: 'Teslim denendi', tone: 'warning' },
  Exception: { text: 'Sorun oluştu', tone: 'danger' },
  ReturnedToSender: { text: 'Göndericiye iade', tone: 'neutral' },
  Cancelled: { text: 'İptal edildi', tone: 'danger' },
};

export const returnReasonLabels: Record<ReturnReason, string> = {
  Damaged: 'Hasarlı geldi',
  Defective: 'Ürün arızalı',
  NotAsDescribed: 'Açıklamaya uymuyor',
  WrongItemSent: 'Yanlış ürün gönderildi',
  SizeTooSmall: 'Beden küçük geldi',
  SizeTooLarge: 'Beden büyük geldi',
  ChangedMind: 'Vazgeçtim',
  ArrivedLate: 'Geç ulaştı',
  MissingParts: 'Eksik parça',
  Other: 'Diğer',
};

export const returnDispositionLabels: Record<ReturnDisposition, string> = {
  Restock: 'Stoğa geri al',
  Damaged: 'Hasarlı (stoğa girmez)',
  Discard: 'İmha et',
  ReturnToSupplier: 'Tedarikçiye iade',
};

export const paymentMethodLabels: Record<PaymentMethodType, string> = {
  CreditCard: 'Kredi kartı',
  DebitCard: 'Banka kartı',
  BankTransfer: 'Havale / EFT',
  CashOnDelivery: 'Kapıda ödeme',
  GiftCard: 'Hediye kartı',
  StoreCredit: 'Mağaza kredisi',
  DigitalWallet: 'Dijital cüzdan',
  BuyNowPayLater: 'Şimdi al sonra öde',
  Installment: 'Taksitli ödeme',
};

export const discountTypeLabels: Record<DiscountType, string> = {
  Percentage: 'Yüzde indirim',
  FixedAmount: 'Tutar indirimi',
  FreeShipping: 'Ücretsiz kargo',
  BuyXGetY: 'X al Y öde',
};

export const bulkStatusLabels: Record<BulkOperationStatus, StatusLabel> = {
  Queued: { text: 'Sırada', tone: 'neutral' },
  Running: { text: 'Çalışıyor', tone: 'info' },
  Completed: { text: 'Tamamlandı', tone: 'success' },
  CompletedWithErrors: { text: 'Hatalarla bitti', tone: 'warning' },
  Failed: { text: 'Başarısız', tone: 'danger' },
  Cancelled: { text: 'İptal edildi', tone: 'neutral' },
};

export const contactStatusLabels: Record<ContactMessageStatus, StatusLabel> = {
  New: { text: 'Yeni', tone: 'info' },
  Read: { text: 'Okundu', tone: 'neutral' },
  Replied: { text: 'Yanıtlandı', tone: 'success' },
  Closed: { text: 'Kapandı', tone: 'neutral' },
  Spam: { text: 'Spam', tone: 'danger' },
};

export const reviewStatusLabels: Record<ReviewStatus, StatusLabel> = {
  Pending: { text: 'Onay bekliyor', tone: 'warning' },
  Approved: { text: 'Yayında', tone: 'success' },
  Rejected: { text: 'Reddedildi', tone: 'danger' },
  Spam: { text: 'Spam', tone: 'danger' },
};

/** Bilinmeyen bir enum degeri gelirse uygulama patlamasin. */
export function labelOf<T extends string>(
  map: Record<string, StatusLabel>,
  value: T | null | undefined,
): StatusLabel {
  if (!value) return { text: '—', tone: 'neutral' };
  return map[value] ?? { text: value, tone: 'neutral' };
}

/** Sozluk disi enum degerleri icin duz metin karsiligi. */
export function textOf<T extends string>(
  map: Record<string, string>,
  value: T | null | undefined,
): string {
  if (!value) return '—';
  return map[value] ?? value;
}
